import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Modal from "@material-ui/core/Modal";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { Link } from "react-router-dom";
import OpenInNew from "@material-ui/icons/OpenInNew";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import AssetBreadcrumb from "components/AssetBreadcrumb/AssetBreadcrumb.jsx";
import AssetStatusChips from "components/AssetStatusChips/AssetStatusChips.jsx";
import Button from "components/CustomButtons/Button.jsx";
import {
  createAsset,
  createAssetAgent,
  createAssetAttribute,
  deleteAssetAttribute,
  getAsset,
  getAssetAgents,
  getAssetTypes,
  getAssets,
  updateAsset,
  updateAssetAgent,
  updateAssetAttribute
} from "services/assetDataProvider";
import { getUserDevices } from "services/dashboardDataProvider";
import { getInlinePageChrome } from "theme/appShellTheme.js";

const { ui, shell, isDark } = getInlinePageChrome();

const styles = {
  pageShell: shell.pageShell,
  cardShell: shell.cardHover,
  cardHeader: shell.cardHeaderHover,
  cardBody: {
    padding: "18px 22px 24px"
  },
  title: {
    color: ui.text,
    margin: 0,
    fontWeight: 700,
    letterSpacing: "0.02em"
  },
  infoPanel: {
    padding: "18px 20px",
    background: ui.surfaceRaised,
    boxShadow: ui.shadowInset,
    borderRadius: 24,
    border: isDark ? `1px solid ${ui.border}` : undefined
  },
  helperText: {
    color: ui.textMuted
  },
  listItem: {
    marginBottom: 10,
    paddingBottom: 10,
    borderBottom: `1px solid ${ui.border}`
  },
  childLayoutShell: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(320px, 1fr)",
    gap: 16,
    alignItems: "start"
  },
  layoutFrame: {
    position: "relative",
    minHeight: 420,
    borderRadius: 24,
    overflow: "hidden",
    background: isDark
      ? `radial-gradient(circle at top left, rgba(128,163,255,0.1), transparent 28%), linear-gradient(180deg, ${ui.surface} 0%, #1a1a1a 100%)`
      : "radial-gradient(circle at top left, rgba(96,165,250,0.18), transparent 28%), linear-gradient(180deg, rgba(255,255,255,0.98) 0%, #eef6ff 100%)",
    border: `1px solid ${ui.border}`,
    boxShadow: ui.shadowInset,
    marginTop: 4
  },
  layoutNode: placement => ({
    position: "absolute",
    left: placement.x,
    top: placement.y,
    width: placement.width,
    height: placement.height,
    borderRadius: 16,
    border: `1px solid ${ui.border}`,
    background: ui.surfaceRaised,
    boxShadow: isDark ? "none" : "0 12px 24px rgba(37, 99, 235, 0.1)",
    padding: "8px 10px",
    overflow: "hidden"
  }),
  layoutNodeLabel: {
    color: ui.text,
    fontWeight: 700,
    fontSize: 13,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  layoutNodeMeta: {
    color: ui.textMuted,
    fontSize: 11,
    lineHeight: 1.3,
    marginTop: 3
  },
  layoutNodeAction: {
    position: "absolute",
    top: 6,
    right: 6,
    minWidth: "auto",
    padding: "2px 8px",
    fontSize: 10,
    lineHeight: 1.2,
    textTransform: "none",
    borderRadius: 999
  },
  layoutResizeHandle: {
    position: "absolute",
    right: 6,
    bottom: 6,
    width: 14,
    height: 14,
    borderRadius: 4,
    border: `1px solid ${ui.accent}`,
    background: ui.surfaceSolid,
    cursor: "nwse-resize",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.15)"
  },
  layoutNodeSelected: {
    outline: `2px solid ${isDark ? "rgba(128, 163, 255, 0.45)" : "rgba(37, 99, 235, 0.32)"}`,
    outlineOffset: 2
  },
  layoutEditorPanel: {
    padding: 16,
    borderRadius: 24,
    background: ui.surfaceRaised,
    boxShadow: ui.shadowInset,
    border: isDark ? `1px solid ${ui.border}` : undefined,
    display: "grid",
    gap: 10
  },
  layoutEditorActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8
  }
};

function buildChain(asset, assetById) {
  const chain = [];
  let cursor = asset;
  while (cursor) {
    chain.unshift(cursor);
    if (!cursor.parent) {
      break;
    }
    cursor = assetById[cursor.parent];
  }
  return chain;
}

function toSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function getLayoutPlacement(asset) {
  const metadata = asset.metadata || {};
  const profileLayouts = metadata.layout_profiles || {};
  const layoutData = profileLayouts.default || metadata.layout || {};

  return {
    x: Number(layoutData.x || 40),
    y: Number(layoutData.y || 40),
    width: Number(layoutData.width || 150),
    height: Number(layoutData.height || 56),
    rotation: Number(layoutData.rotation || 0),
    label: layoutData.label || asset.name
  };
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function snapToGrid(value, gridSize = 20) {
  return Math.round(value / gridSize) * gridSize;
}

function getAssetTypeLabel(assetItem, assetTypeLookup) {
  if (!assetItem) {
    return "-";
  }

  if (assetItem.asset_type && typeof assetItem.asset_type === "object") {
    const nestedTypeId = assetItem.asset_type.id == null ? "" : String(assetItem.asset_type.id);
    const nestedMatchedType = assetTypeLookup.get(nestedTypeId);
    if (nestedMatchedType) {
      return (
        nestedMatchedType.name ||
        nestedMatchedType.label ||
        nestedMatchedType.slug ||
        String(nestedMatchedType.id || "-")
      );
    }

    return (
      assetItem.asset_type.name ||
      assetItem.asset_type.label ||
      assetItem.asset_type.slug ||
      String(assetItem.asset_type.id || "-")
    );
  }

  const assetTypeKey = assetItem.asset_type == null ? "" : String(assetItem.asset_type);
  const matchedType = assetTypeLookup.get(assetTypeKey);
  if (matchedType) {
    return matchedType.name || matchedType.label || matchedType.slug || String(matchedType.id || "-");
  }

  return assetItem.asset_type ? String(assetItem.asset_type) : "-";
}

function getAgentMappingRules(agentItem) {
  if (Array.isArray(agentItem.attribute_mappings) && agentItem.attribute_mappings.length > 0) {
    return agentItem.attribute_mappings;
  }

  const legacyTemplate = agentItem.attribute_mapping_template || {};
  return Object.keys(legacyTemplate).map(targetKey => ({
    source_key: legacyTemplate[targetKey],
    target_key: targetKey,
    transform: null
  }));
}

function normalizeDevicesPayload(payload) {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.devices)) {
    return payload.devices;
  }
  if (Array.isArray(payload.results)) {
    return payload.results;
  }
  return [];
}

function clampLayoutPlacement(placement, frameWidth = 880, frameHeight = 420) {
  return {
    ...placement,
    x: clamp(snapToGrid(Number(placement.x || 40)), 0, frameWidth - Number(placement.width || 150)),
    y: clamp(snapToGrid(Number(placement.y || 40)), 0, frameHeight - Number(placement.height || 56)),
    width: clamp(snapToGrid(Number(placement.width || 150)), 100, 1000),
    height: clamp(snapToGrid(Number(placement.height || 56)), 40, 1000),
    rotation: Number(placement.rotation || 0),
    label: placement.label || ""
  };
}

export default function AssetDetails({ axios, match }) {
  const assetId = match.params.assetId;
  const [asset, setAsset] = useState(null);
  const [allAssets, setAllAssets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [devices, setDevices] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [savingAsset, setSavingAsset] = useState(false);
  const [savingChildLayout, setSavingChildLayout] = useState(false);
  const [savingAgent, setSavingAgent] = useState(false);
  const [savingAttribute, setSavingAttribute] = useState(false);
  const [deletingAttributeId, setDeletingAttributeId] = useState("");
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [attributeModalOpen, setAttributeModalOpen] = useState(false);
  const [assetMode, setAssetMode] = useState("edit");
  const [agentMode, setAgentMode] = useState("create");
  const [attributeMode, setAttributeMode] = useState("create");
  const [currentAgent, setCurrentAgent] = useState(null);
  const [currentAttribute, setCurrentAttribute] = useState(null);
  const [selectedChildId, setSelectedChildId] = useState("");
  const [childLayoutDrafts, setChildLayoutDrafts] = useState({});
  const [childLayoutDraft, setChildLayoutDraft] = useState({
    x: "",
    y: "",
    label: "",
    width: "",
    height: "",
    rotation: ""
  });
  const [assetForm, setAssetForm] = useState({
    name: "",
    code: "",
    description: "",
    asset_type: "",
    address: "",
    lat: "",
    lng: "",
    boundary_geojson: "",
    active: true
  });
  const [agentForm, setAgentForm] = useState({
    name: "",
    source_type: "device_status",
    source_asset: "",
    sourceDeviceIds: [],
    sync_mode: "on_read",
    active: true,
    sourceKey: "",
    targetKey: "",
    transform: ""
  });
  const [attributeForm, setAttributeForm] = useState({
    key: "",
    label: "",
    value_type: "text",
    unit: "",
    static_value: ""
  });
  const [error, setError] = useState("");
  const [agentError, setAgentError] = useState("");
  const [attributeError, setAttributeError] = useState("");
  const layoutInteractionRef = useRef(null);
  const childLayoutDraftsRef = useRef({});

  useEffect(() => {
    childLayoutDraftsRef.current = childLayoutDrafts;
  }, [childLayoutDrafts]);

  const load = () => {
    return Promise.all([
      getAsset(axios, assetId),
      getAssets(axios),
      getAssetAgents(axios, assetId),
      getAssetTypes(axios),
      getUserDevices(axios, { page_size: 1000 })
    ]).then(([assetData, assetsData, agentsData, typesData, devicesData]) => {
      setAsset(assetData);
      setAllAssets(assetsData || []);
      setAgents(agentsData || []);
      setAssetTypes(typesData || []);
      setDevices(normalizeDevicesPayload(devicesData));
    });
  };

  useEffect(() => {
    load();
  }, [axios, assetId]);

  const assetById = useMemo(() => {
    const next = {};
    allAssets.forEach(item => {
      next[item.id] = item;
    });
    return next;
  }, [allAssets]);

  const childAssets = useMemo(() => {
    return allAssets.filter(item => item.parent === assetId);
  }, [allAssets, assetId]);

  const childAssetPlacements = useMemo(() => {
    return childAssets.map(child => ({
      asset: child,
      placement: getLayoutPlacement(child)
    }));
  }, [childAssets]);

  const selectedChild = useMemo(() => {
    return childAssets.find(child => child.id === selectedChildId) || null;
  }, [childAssets, selectedChildId]);

  const selectedChildPlacement = useMemo(() => {
    if (!selectedChild) {
      return null;
    }

    return childAssetPlacements.find(entry => entry.asset.id === selectedChild.id) || null;
  }, [childAssetPlacements, selectedChild]);

  useEffect(() => {
    if (childAssets.length > 0 && !selectedChildId) {
      setSelectedChildId(childAssets[0].id);
    }
    if (childAssets.length === 0) {
      setSelectedChildId("");
    }
  }, [childAssets, selectedChildId]);

  useEffect(() => {
    if (!selectedChild) {
      setChildLayoutDraft({ x: "", y: "", label: "", width: "", height: "", rotation: "" });
      return;
    }

    const existingDraft = childLayoutDraftsRef.current[selectedChild.id];
    if (existingDraft) {
      setChildLayoutDraft(existingDraft);
      return;
    }

    const placement = clampLayoutPlacement(selectedChildPlacement ? selectedChildPlacement.placement : getLayoutPlacement(selectedChild));
    const nextDraft = {
      x: String(placement.x),
      y: String(placement.y),
      label: placement.label || selectedChild.name || "",
      width: String(placement.width),
      height: String(placement.height),
      rotation: String(placement.rotation || 0)
    };
    setChildLayoutDraft(nextDraft);
    setChildLayoutDrafts(prev => ({
      ...prev,
      [selectedChild.id]: nextDraft
    }));
  }, [selectedChild, selectedChildId, selectedChildPlacement]);

  const selectedChildPlacementPreview = useMemo(() => {
    if (!selectedChildPlacement) {
      return null;
    }

    return {
      ...selectedChildPlacement.placement,
      x: Number(childLayoutDraft.x || selectedChildPlacement.placement.x || 40),
      y: Number(childLayoutDraft.y || selectedChildPlacement.placement.y || 40),
      width: Number(childLayoutDraft.width || selectedChildPlacement.placement.width || 150),
      height: Number(childLayoutDraft.height || selectedChildPlacement.placement.height || 56),
      rotation: Number(childLayoutDraft.rotation || selectedChildPlacement.placement.rotation || 0),
      label: childLayoutDraft.label || selectedChildPlacement.placement.label || selectedChild.name || ""
    };
  }, [childLayoutDraft, selectedChild, selectedChildPlacement]);

  const updateChildLayoutDraft = (childId, nextPlacement) => {
    const nextDraft = {
      x: String(nextPlacement.x),
      y: String(nextPlacement.y),
      width: String(nextPlacement.width),
      height: String(nextPlacement.height),
      rotation: String(nextPlacement.rotation || 0),
      label: nextPlacement.label || ""
    };

    setChildLayoutDraft(nextDraft);
    if (childId) {
      setChildLayoutDrafts(prev => ({
        ...prev,
        [childId]: nextDraft
      }));
    }
  };

  const beginLayoutInteraction = (mode, childAsset) => event => {
    event.preventDefault();
    event.stopPropagation();

    const basePlacement =
      (selectedChild && selectedChild.id === childAsset.id && selectedChildPlacementPreview) ||
      getLayoutPlacement(childAsset);

    const placement = clampLayoutPlacement(basePlacement);
    setSelectedChildId(childAsset.id);
    updateChildLayoutDraft(childAsset.id, {
      ...placement,
      label: placement.label || childAsset.name
    });

    layoutInteractionRef.current = {
      mode,
      childId: childAsset.id,
      startX: event.clientX,
      startY: event.clientY,
      startPlacement: placement
    };
  };

  useEffect(() => {
    const handlePointerMove = event => {
      const interaction = layoutInteractionRef.current;
      if (!interaction) {
        return;
      }

      const draggedChild = childAssets.find(item => item.id === interaction.childId);
      if (!draggedChild) {
        return;
      }

      const deltaX = event.clientX - interaction.startX;
      const deltaY = event.clientY - interaction.startY;
      const nextPlacement = { ...interaction.startPlacement };

      if (interaction.mode === "drag") {
        nextPlacement.x = clamp(
          snapToGrid(interaction.startPlacement.x + deltaX),
          0,
          880 - interaction.startPlacement.width
        );
        nextPlacement.y = clamp(
          snapToGrid(interaction.startPlacement.y + deltaY),
          0,
          420 - interaction.startPlacement.height
        );
      }

      if (interaction.mode === "resize") {
        nextPlacement.width = clamp(
          snapToGrid(interaction.startPlacement.width + deltaX),
          100,
          1000
        );
        nextPlacement.height = clamp(
          snapToGrid(interaction.startPlacement.height + deltaY),
          40,
          1000
        );
        nextPlacement.x = clamp(nextPlacement.x, 0, 880 - nextPlacement.width);
        nextPlacement.y = clamp(nextPlacement.y, 0, 420 - nextPlacement.height);
      }

      updateChildLayoutDraft(interaction.childId, {
        ...nextPlacement,
        label: interaction.startPlacement.label || draggedChild.name || ""
      });
    };

    const handlePointerUp = () => {
      if (!layoutInteractionRef.current) {
        return;
      }
      layoutInteractionRef.current = null;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [selectedChild]);

  const selectedChildDragStart = childId => event => {
    event.dataTransfer.setData("child-asset-id", childId);
  };

  const onChildCanvasDrop = event => {
    event.preventDefault();
    const childId = event.dataTransfer.getData("child-asset-id");
    if (!childId) {
      return;
    }

    const child = childAssets.find(item => item.id === childId);
    if (!child) {
      return;
    }

    const canvasRect = event.currentTarget.getBoundingClientRect();
    const currentPlacement =
      (selectedChild && selectedChild.id === childId && selectedChildPlacementPreview) ||
      getLayoutPlacement(child);
    const x = snapToGrid(
      clamp(
        event.clientX - canvasRect.left - currentPlacement.width / 2,
        0,
        canvasRect.width - currentPlacement.width
      )
    );
    const y = snapToGrid(
      clamp(
        event.clientY - canvasRect.top - currentPlacement.height / 2,
        0,
        canvasRect.height - currentPlacement.height
      )
    );

    setSelectedChildId(childId);
    setChildLayoutDraft(prev => ({
      ...prev,
      x: String(x),
      y: String(y),
      label: prev.label || child.name
    }));
  };

  const moveSelectedChild = (dx, dy) => {
    if (!selectedChildPlacementPreview) {
      return;
    }

    const nextX = snapToGrid(clamp(selectedChildPlacementPreview.x + dx, 0, 880 - selectedChildPlacementPreview.width));
    const nextY = snapToGrid(clamp(selectedChildPlacementPreview.y + dy, 0, 420 - selectedChildPlacementPreview.height));
    setChildLayoutDraft(prev => ({
      ...prev,
      x: String(nextX),
      y: String(nextY)
    }));
  };

  const saveSelectedChildLayout = () => {
    if (!selectedChild) {
      return;
    }

    const savedDraft = childLayoutDraftsRef.current[selectedChild.id] || childLayoutDraft;
    const placementSource = {
      x: Number(savedDraft.x || selectedChildPlacementPreview?.x || 40),
      y: Number(savedDraft.y || selectedChildPlacementPreview?.y || 40),
      width: Number(savedDraft.width || selectedChildPlacementPreview?.width || 150),
      height: Number(savedDraft.height || selectedChildPlacementPreview?.height || 56),
      rotation: Number(savedDraft.rotation || selectedChildPlacementPreview?.rotation || 0),
      label: savedDraft.label || selectedChildPlacementPreview?.label || selectedChild.name
    };

    setSavingChildLayout(true);
    setError("");

    const nextPlacement = {
      x: clamp(snapToGrid(placementSource.x), 0, 880 - placementSource.width),
      y: clamp(snapToGrid(placementSource.y), 0, 420 - placementSource.height),
      width: clamp(snapToGrid(placementSource.width), 100, 1000),
      height: clamp(snapToGrid(placementSource.height), 40, 1000),
      rotation: placementSource.rotation,
      label: placementSource.label
    };

    const existingMeta = selectedChild.metadata || {};
    const nextMeta = {
      ...existingMeta,
      layout_profiles: {
        ...(existingMeta.layout_profiles || {}),
        default: nextPlacement
      },
      layout: nextPlacement
    };

    updateAsset(axios, selectedChild.id, { metadata: nextMeta })
      .then(() => load())
      .then(() => {
        setChildLayoutDrafts(prev => ({
          ...prev,
          [selectedChild.id]: {
            x: String(nextPlacement.x),
            y: String(nextPlacement.y),
            label: nextPlacement.label || selectedChild.name || "",
            width: String(nextPlacement.width),
            height: String(nextPlacement.height),
            rotation: String(nextPlacement.rotation || 0)
          }
        }));
        setError("");
      })
      .catch(err => {
        setError(err.message || "Failed to save child layout.");
      })
      .finally(() => {
        setSavingChildLayout(false);
      });
  };

  const assetTypeById = useMemo(() => {
    const lookup = new Map();
    assetTypes.forEach(type => {
      lookup.set(String(type.id), type);
      if (type.slug) {
        lookup.set(String(type.slug), type);
      }
      if (type.name) {
        lookup.set(String(type.name), type);
      }
    });
    return lookup;
  }, [assetTypes]);

  const selectedAssetTypeLabel = useMemo(() => {
    return getAssetTypeLabel(asset, assetTypeById);
  }, [asset, assetTypeById]);

  const openEditAsset = () => {
    if (!asset) {
      return;
    }

    setAssetMode("edit");
    setError("");
    const loc = asset.location || {};
    setAssetForm({
      name: asset.name || "",
      code: asset.code || "",
      description: asset.description || "",
      asset_type: asset.asset_type || "",
      address: asset.address || "",
      lat: loc.lat != null ? String(loc.lat) : "",
      lng: loc.lng != null ? String(loc.lng) : "",
      boundary_geojson: asset.boundary_geojson ? JSON.stringify(asset.boundary_geojson, null, 2) : "",
      active: asset.active !== false
    });
    setAssetModalOpen(true);
  };

  const openCreateChildAsset = () => {
    if (!asset) {
      return;
    }

    setAssetMode("child");
    setError("");
    setAssetForm({
      name: "",
      code: "",
      description: "",
      asset_type: asset.asset_type && typeof asset.asset_type === "object"
        ? asset.asset_type.id || ""
        : asset.asset_type || "",
      active: true
    });
    setAssetModalOpen(true);
  };

  const openCreateAgent = () => {
    setCurrentAgent(null);
    setAgentMode("create");
    setAgentError("");
    setAgentForm({
      name: "",
      source_type: "device_status",
      source_asset: "",
      sourceDeviceIds: [],
      sync_mode: "on_read",
      active: true,
      sourceKey: "",
      targetKey: "",
      transform: ""
    });
    setAgentModalOpen(true);
  };

  const openEditAgent = agentItem => {
    const mappingRules = getAgentMappingRules(agentItem);
    const firstRule = mappingRules[0] || {};
    const allowedDeviceIds = new Set((devices || []).map(item => String(item.id)));
    const selectedDeviceIds = (agentItem.devices || [])
      .map(deviceId => String(deviceId))
      .filter(deviceId => allowedDeviceIds.has(deviceId));

    setCurrentAgent(agentItem);
    setAgentMode("edit");
    setAgentError("");
    setAgentForm({
      name: agentItem.name || "",
      source_type: agentItem.source_type || "device_status",
      source_asset: agentItem.source_asset || "",
      sourceDeviceIds: selectedDeviceIds,
      sync_mode: agentItem.sync_mode || "on_read",
      active: agentItem.active !== false,
      sourceKey: firstRule.source_key || "",
      targetKey: firstRule.target_key || "",
      transform: firstRule.transform || ""
    });
    setAgentModalOpen(true);
  };

  const openCreateAttribute = () => {
    setCurrentAttribute(null);
    setAttributeMode("create");
    setAttributeError("");
    setAttributeForm({
      key: "",
      label: "",
      value_type: "text",
      unit: "",
      static_value: ""
    });
    setAttributeModalOpen(true);
  };

  const openEditAttribute = attributeItem => {
    setCurrentAttribute(attributeItem);
    setAttributeMode("edit");
    setAttributeError("");
    setAttributeForm({
      key: attributeItem.key || "",
      label: attributeItem.label || "",
      value_type: attributeItem.value_type || "text",
      unit: attributeItem.unit || "",
      static_value:
        attributeItem.static_value == null
          ? ""
          : typeof attributeItem.static_value === "string"
          ? attributeItem.static_value
          : JSON.stringify(attributeItem.static_value)
    });
    setAttributeModalOpen(true);
  };

  const saveAsset = () => {
    if (!assetForm.name || !assetForm.asset_type) {
      setError("Name and asset type are required.");
      return;
    }

    setSavingAsset(true);
    setError("");

    const payload = {
      name: assetForm.name,
      code: assetForm.code || null,
      description: assetForm.description || null,
      asset_type: assetForm.asset_type,
      address: assetForm.address || null,
      active: assetForm.active
    };

    const latNum = parseFloat(assetForm.lat);
    const lngNum = parseFloat(assetForm.lng);
    if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
      payload.location = { lat: latNum, lng: lngNum };
    } else if (assetMode === "edit") {
      payload.location = null;
    }

    if (assetForm.boundary_geojson && assetForm.boundary_geojson.trim()) {
      try {
        payload.boundary_geojson = JSON.parse(assetForm.boundary_geojson);
      } catch (_) {
        setError("Boundary GeoJSON is not valid JSON.");
        setSavingAsset(false);
        return;
      }
    } else if (assetMode === "edit") {
      payload.boundary_geojson = null;
    }

    if (assetMode === "child") {
      payload.parent = asset.id;
    }

    const action = assetMode === "edit" ? updateAsset(axios, asset.id, payload) : createAsset(axios, payload);
    action
      .then(() => load())
      .then(() => {
        setAssetModalOpen(false);
      })
      .catch(err => {
        setError(err.message || "Failed to save asset.");
      })
      .finally(() => {
        setSavingAsset(false);
      });
  };

  const saveAgent = () => {
    if (!agentForm.name) {
      setAgentError("Agent name is required.");
      return;
    }

    if (agentForm.source_type === "asset_attribute" && !agentForm.source_asset) {
      setAgentError("Source asset is required for asset attribute bindings.");
      return;
    }

    setSavingAgent(true);
    setAgentError("");

    const mappingRules =
      agentForm.sourceKey && agentForm.targetKey
        ? [
            {
              source_key: agentForm.sourceKey,
              target_key: agentForm.targetKey,
              transform: agentForm.transform || null
            }
          ]
        : [];

    const payload = {
      name: agentForm.name,
      asset: asset.id,
      source_type: agentForm.source_type,
      source_asset:
        agentForm.source_type === "asset_attribute" && agentForm.source_asset
          ? agentForm.source_asset
          : null,
      devices:
        agentForm.source_type === "device_status"
          ? agentForm.sourceDeviceIds.map(item => String(item))
          : [],
      sync_mode: agentForm.sync_mode,
      active: agentForm.active,
      attribute_mappings: mappingRules,
      attribute_mapping_template:
        agentForm.sourceKey && agentForm.targetKey
          ? { [agentForm.targetKey]: agentForm.sourceKey }
          : {}
    };

    const action = agentMode === "edit"
      ? updateAssetAgent(axios, currentAgent.id, payload)
      : createAssetAgent(axios, payload);

    action
      .then(() => load())
      .then(() => {
        setAgentModalOpen(false);
      })
      .catch(err => {
        setAgentError(err.message || "Failed to save binding agent.");
      })
      .finally(() => {
        setSavingAgent(false);
      });
  };

  const saveAttribute = () => {
    if (!attributeForm.key || !attributeForm.label) {
      setAttributeError("Key and label are required.");
      return;
    }

    setSavingAttribute(true);
    setAttributeError("");

    const payload = {
      key: attributeForm.key,
      label: attributeForm.label,
      value_type: attributeForm.value_type,
      unit: attributeForm.unit || null,
      static_value: attributeForm.static_value || null
    };

    const action = attributeMode === "edit" && currentAttribute
      ? updateAssetAttribute(axios, currentAttribute.id, payload)
      : createAssetAttribute(axios, asset.id, payload);

    action
      .then(() => load())
      .then(() => {
        setAttributeModalOpen(false);
      })
      .catch(err => {
        setAttributeError(err.message || "Failed to save attribute.");
      })
      .finally(() => {
        setSavingAttribute(false);
      });
  };

  const removeAttribute = attributeId => {
    if (!window.confirm("Delete this attribute?")) {
      return;
    }

    setDeletingAttributeId(attributeId);
    deleteAssetAttribute(axios, attributeId)
      .then(() => load())
      .finally(() => {
        setDeletingAttributeId("");
      });
  };

  if (!asset) {
    return <div>Loading asset details...</div>;
  }

  const chain = buildChain(asset, assetById);

  return (
    <div style={styles.pageShell}>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <AssetBreadcrumb chain={chain} />
        </GridItem>
        <GridItem xs={12} sm={12} md={12}>
          <Card style={styles.cardShell}>
            <CardHeader color="transparent" style={styles.cardHeader}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <h4 style={styles.title}>{asset.name}</h4>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button color="info" size="sm" onClick={openEditAsset}>Edit Asset</Button>
                  <Button color="info" size="sm" onClick={openCreateChildAsset}>Create Child Asset</Button>
                  <Button color="info" size="sm" onClick={openCreateAgent}>Add Binding Agent</Button>
                  <Button color="info" size="sm" onClick={openCreateAttribute}>Add Attribute</Button>
                </div>
              </div>
            </CardHeader>
            <CardBody style={styles.cardBody}>
              <div style={styles.infoPanel}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 18px" }}>
                  <div><strong>Code:</strong> {asset.code || "-"}</div>
                  <div><strong>Type:</strong> {selectedAssetTypeLabel}</div>
                  <div style={{ gridColumn: "1 / -1" }}><strong>Description:</strong> {asset.description || "-"}</div>
                  <div style={{ gridColumn: "1 / -1" }}><strong>Address:</strong> {asset.address || "-"}</div>
                  <div>
                    <strong>Location:</strong>{" "}
                    {asset.location
                      ? `lat: ${asset.location.lat}, lng: ${asset.location.lng}`
                      : "-"}
                  </div>
                  <div>
                    <strong>Boundary:</strong>{" "}
                    {asset.boundary_geojson ? "Defined" : "-"}
                  </div>
                  <div>
                    <strong>Created:</strong>{" "}
                    {asset.created_at ? new Date(asset.created_at).toLocaleDateString() : "-"}
                  </div>
                  <div>
                    <strong>Updated:</strong>{" "}
                    {asset.updated_at ? new Date(asset.updated_at).toLocaleDateString() : "-"}
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <AssetStatusChips asset={asset} assetTypeLabel={selectedAssetTypeLabel} />
                </div>
              </div>
            </CardBody>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={12} md={12}>
          <Card style={styles.cardShell}>
            <CardHeader color="transparent" style={styles.cardHeader}>
              <h4 style={styles.title}>Child Assets Layout</h4>
            </CardHeader>
            <CardBody style={styles.cardBody}>
              {childAssetPlacements.length === 0 ? (
                <div style={{ ...styles.infoPanel, color: "#64748b" }}>No child assets have been added yet.</div>
              ) : (
                <div style={styles.childLayoutShell}>
                  <div style={styles.layoutFrame}>
                    <div
                      onDragOver={event => event.preventDefault()}
                      onDrop={onChildCanvasDrop}
                      style={{ position: "relative", width: "100%", minHeight: 420 }}
                    >
                      {childAssetPlacements.map(({ asset: childAsset, placement }) => {
                        const isSelected = childAsset.id === selectedChildId;
                        const draftPlacement = childLayoutDrafts[childAsset.id];
                        const renderedPlacement = isSelected && selectedChildPlacementPreview
                          ? selectedChildPlacementPreview
                          : draftPlacement
                          ? clampLayoutPlacement(draftPlacement)
                          : placement;
                        const isActiveInteraction =
                          layoutInteractionRef.current && layoutInteractionRef.current.childId === childAsset.id;

                        return (
                          <div
                            key={childAsset.id}
                            onClick={() => {
                              if (!layoutInteractionRef.current) {
                                setSelectedChildId(childAsset.id);
                              }
                            }}
                            style={{
                              ...styles.layoutNode(renderedPlacement),
                              ...(isSelected ? styles.layoutNodeSelected : {}),
                              cursor: isActiveInteraction && layoutInteractionRef.current.mode === "resize" ? "nwse-resize" : "move",
                              touchAction: "none"
                            }}
                            onPointerDown={beginLayoutInteraction("drag", childAsset)}
                          >
                            <div style={styles.layoutNodeLabel}>{renderedPlacement.label || childAsset.name}</div>
                            <div style={styles.layoutNodeMeta}>
                              {getAssetTypeLabel(childAsset, assetTypeById)}
                            </div>
                            <div style={styles.layoutNodeMeta}>
                              {renderedPlacement.width} x {renderedPlacement.height}
                            </div>
                            <Button
                              color="info"
                              size="sm"
                              component={Link}
                              to={`/assets/${childAsset.id}`}
                              style={styles.layoutNodeAction}
                              aria-label={`Open ${childAsset.name} details`}
                              onPointerDown={event => event.stopPropagation()}
                            >
                              <OpenInNew style={{ fontSize: 14 }} />
                            </Button>
                            <div
                              style={styles.layoutResizeHandle}
                              aria-label={`Resize ${childAsset.name}`}
                              role="presentation"
                              onPointerDown={beginLayoutInteraction("resize", childAsset)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div style={styles.layoutEditorPanel}>
                    {!selectedChild ? (
                      <div style={styles.helperText}>Select a child asset to edit its layout.</div>
                    ) : (
                      <>
                        <div>
                          <strong style={{ color: "#122033" }}>{selectedChild.name}</strong>
                          <div style={styles.helperText}>{getAssetTypeLabel(selectedChild, assetTypeById)}</div>
                        </div>
                        <TextField
                          fullWidth
                          label="Display Label"
                          variant="outlined"
                          size="small"
                          value={childLayoutDraft.label}
                          onChange={e => setChildLayoutDraft(prev => ({ ...prev, label: e.target.value }))}
                        />
                        <TextField
                          fullWidth
                          label="Size"
                          variant="outlined"
                          size="small"
                          value={`${childLayoutDraft.width || 150} x ${childLayoutDraft.height || 56}`}
                          InputProps={{ readOnly: true }}
                        />
                        <TextField
                          fullWidth
                          label="Position"
                          variant="outlined"
                          size="small"
                          value={`x: ${childLayoutDraft.x || 40}, y: ${childLayoutDraft.y || 40}`}
                          InputProps={{ readOnly: true }}
                        />
                        <TextField
                          fullWidth
                          label="Rotation"
                          variant="outlined"
                          size="small"
                          value={`${childLayoutDraft.rotation || 0} deg`}
                          InputProps={{ readOnly: true }}
                        />
                        <div style={styles.helperText}>Drag the rectangle to move it. Use the bottom-right handle to resize it.</div>
                        <Button color="info" size="sm" onClick={saveSelectedChildLayout} disabled={savingChildLayout}>
                          {savingChildLayout ? "Saving..." : "Save Child Layout"}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <Card style={styles.cardShell}>
            <CardHeader color="transparent" style={styles.cardHeader}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <h4 style={styles.title}>Binding Agents</h4>
                <Button color="info" size="sm" onClick={openCreateAgent}>Add Binding Agent</Button>
              </div>
            </CardHeader>
            <CardBody style={styles.cardBody}>
              <div style={styles.infoPanel}>
                {agents.length === 0 && <div style={styles.helperText}>No binding agents linked to this asset.</div>}
                {agents.map(agent => (
                  <div key={agent.id} style={styles.listItem}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                      <div>
                        <strong>{agent.name}</strong>
                        <div style={styles.helperText}>
                          Source:{" "}
                          {agent.source_type === "asset_attribute"
                            ? ((assetById[agent.source_asset] && assetById[agent.source_asset].name) || "Asset Attribute")
                            : `Device Status (${(agent.devices || []).length})`}
                          {" | "}Sync Mode: {agent.sync_mode}
                          {" | "}Mappings: {getAgentMappingRules(agent).length}
                        </div>
                      </div>
                      <Button color="info" size="sm" onClick={() => openEditAgent(agent)}>Edit</Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <Card style={styles.cardShell}>
            <CardHeader color="transparent" style={styles.cardHeader}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <h4 style={styles.title}>Attributes</h4>
                <Button color="info" size="sm" onClick={openCreateAttribute}>Add Attribute</Button>
              </div>
            </CardHeader>
            <CardBody style={styles.cardBody}>
              <div style={styles.infoPanel}>
                {(asset.attributes || []).length === 0 && <div style={styles.helperText}>No attributes defined.</div>}
                {(asset.attributes || []).map(attr => (
                  <div key={attr.id} style={styles.listItem}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                      <div>
                        <strong>{attr.label || attr.key}</strong>
                        <div style={styles.helperText}>
                          {attr.key} | {attr.value_type}
                          {attr.unit ? ` (${attr.unit})` : ""}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <Button color="info" size="sm" onClick={() => openEditAttribute(attr)}>Edit</Button>
                        <Button color="danger" size="sm" onClick={() => removeAttribute(attr.id)} disabled={deletingAttributeId === attr.id}>
                          {deletingAttributeId === attr.id ? "Deleting..." : "Delete"}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      <Modal open={assetModalOpen} onClose={() => setAssetModalOpen(false)}>
        <div style={{ width: 560, maxWidth: "92vw", margin: "8vh auto", background: "white", borderRadius: 14, padding: 18, outline: "none", display: "grid", gap: 12 }}>
          <h4 style={styles.title}>{assetMode === "edit" ? "Edit Asset" : "Create Child Asset"}</h4>
          <TextField label="Asset Name" variant="outlined" size="small" value={assetForm.name} onChange={e => setAssetForm({ ...assetForm, name: e.target.value })} />
          <TextField label="Asset Code" variant="outlined" size="small" value={assetForm.code} onChange={e => setAssetForm({ ...assetForm, code: e.target.value })} />
          <TextField label="Description" variant="outlined" size="small" multiline rows={2} value={assetForm.description} onChange={e => setAssetForm({ ...assetForm, description: e.target.value })} />
          <TextField
            select
            label="Asset Type"
            variant="outlined"
            size="small"
            SelectProps={{ native: true }}
            value={assetForm.asset_type}
            onChange={e => setAssetForm({ ...assetForm, asset_type: e.target.value })}
          >
            <option value="">Select asset type</option>
            {assetTypes.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </TextField>
          <TextField label="Address" variant="outlined" size="small" multiline rows={2} value={assetForm.address} onChange={e => setAssetForm({ ...assetForm, address: e.target.value })} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <TextField label="Latitude" variant="outlined" size="small" type="number" value={assetForm.lat} onChange={e => setAssetForm({ ...assetForm, lat: e.target.value })} />
            <TextField label="Longitude" variant="outlined" size="small" type="number" value={assetForm.lng} onChange={e => setAssetForm({ ...assetForm, lng: e.target.value })} />
          </div>
          <TextField
            label="Boundary GeoJSON (optional)"
            variant="outlined"
            size="small"
            multiline
            rows={3}
            placeholder='{"type":"Polygon","coordinates":[[[0,0],[1,0],[1,1],[0,1],[0,0]]]}'
            value={assetForm.boundary_geojson}
            onChange={e => setAssetForm({ ...assetForm, boundary_geojson: e.target.value })}
          />
          <FormControlLabel
            control={<Switch checked={assetForm.active} onChange={e => setAssetForm({ ...assetForm, active: e.target.checked })} color="primary" />}
            label="Active"
          />
          {error ? <div style={{ color: "#b91c1c" }}>{error}</div> : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button color="white" onClick={() => setAssetModalOpen(false)}>Cancel</Button>
            <Button color="info" onClick={saveAsset} disabled={savingAsset}>{savingAsset ? "Saving..." : "Save"}</Button>
          </div>
        </div>
      </Modal>

      <Modal open={agentModalOpen} onClose={() => setAgentModalOpen(false)}>
        <div style={{ width: 560, maxWidth: "92vw", margin: "8vh auto", background: "white", borderRadius: 14, padding: 18, outline: "none", display: "grid", gap: 12 }}>
          <h4 style={styles.title}>{agentMode === "edit" ? "Edit Binding Agent" : "Add Binding Agent"}</h4>
          <TextField label="Agent Name" variant="outlined" size="small" value={agentForm.name} onChange={e => setAgentForm({ ...agentForm, name: e.target.value })} />
          <TextField
            select
            label="Source Type"
            variant="outlined"
            size="small"
            SelectProps={{ native: true }}
            value={agentForm.source_type}
            onChange={e =>
              setAgentForm({
                ...agentForm,
                source_type: e.target.value,
                source_asset: "",
                sourceDeviceIds: []
              })
            }
          >
            <option value="device_status">Device Status</option>
            <option value="asset_attribute">Asset Attribute</option>
          </TextField>
          {agentForm.source_type === "asset_attribute" ? (
            <TextField
              select
              label="Source Asset"
              variant="outlined"
              size="small"
              SelectProps={{ native: true }}
              value={agentForm.source_asset}
              onChange={e => setAgentForm({ ...agentForm, source_asset: e.target.value })}
            >
              <option value="">Select source asset</option>
              {allAssets
                .filter(item => item.id !== asset.id)
                .map(item => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </TextField>
          ) : (
            <>
              <TextField
                select
                label="Source Devices"
                variant="outlined"
                size="small"
                SelectProps={{
                  native: true,
                  multiple: true,
                  onChange: e => {
                    const sourceDeviceIds = Array.from(e.target.options)
                      .filter(option => option.selected)
                      .map(option => option.value);
                    setAgentForm({ ...agentForm, sourceDeviceIds });
                  }
                }}
                value={agentForm.sourceDeviceIds}
                helperText="Only devices you can access are listed"
              >
                {devices.length === 0 ? (
                  <option disabled value="">
                    No accessible devices found
                  </option>
                ) : null}
                {devices.map(item => (
                  <option key={String(item.id)} value={String(item.id)}>
                    {item.alias || item.name || item.ip_address || String(item.id)}
                  </option>
                ))}
              </TextField>
              {agentForm.sourceDeviceIds.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {agentForm.sourceDeviceIds.map(deviceId => {
                    const matchedDevice = devices.find(item => String(item.id) === String(deviceId));
                    const label = matchedDevice
                      ? matchedDevice.alias || matchedDevice.name || matchedDevice.ip_address || String(matchedDevice.id)
                      : String(deviceId);
                    return (
                      <span
                        key={String(deviceId)}
                        style={{
                          padding: "3px 10px",
                          borderRadius: 999,
                          fontSize: 12,
                          background: "#e0f2fe",
                          color: "#0c4a6e",
                          border: "1px solid #bae6fd"
                        }}
                      >
                        {label}
                      </span>
                    );
                  })}
                </div>
              ) : null}
            </>
          )}
          <TextField
            select
            label="Sync Mode"
            variant="outlined"
            size="small"
            SelectProps={{ native: true }}
            value={agentForm.sync_mode}
            onChange={e => setAgentForm({ ...agentForm, sync_mode: e.target.value })}
          >
            <option value="on_read">On Read</option>
            <option value="periodic">Periodic</option>
            <option value="manual">Manual</option>
          </TextField>
          <TextField
            label="Source Key"
            variant="outlined"
            size="small"
            helperText={
              agentForm.source_type === "asset_attribute"
                ? "Attribute key on source asset"
                : "Device status field key"
            }
            value={agentForm.sourceKey}
            onChange={e => setAgentForm({ ...agentForm, sourceKey: e.target.value })}
          />
          <TextField
            label="Target Key"
            variant="outlined"
            size="small"
            helperText="Destination attribute key on this asset"
            value={agentForm.targetKey}
            onChange={e => setAgentForm({ ...agentForm, targetKey: e.target.value })}
          />
          <TextField
            label="Transform (optional)"
            variant="outlined"
            size="small"
            value={agentForm.transform}
            onChange={e => setAgentForm({ ...agentForm, transform: e.target.value })}
          />
          {agentError ? <div style={{ color: "#b91c1c" }}>{agentError}</div> : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button color="white" onClick={() => setAgentModalOpen(false)}>Cancel</Button>
            <Button color="info" onClick={saveAgent} disabled={savingAgent}>{savingAgent ? "Saving..." : "Save"}</Button>
          </div>
        </div>
      </Modal>

      <Modal open={attributeModalOpen} onClose={() => setAttributeModalOpen(false)}>
        <div style={{ width: 560, maxWidth: "92vw", margin: "8vh auto", background: "white", borderRadius: 14, padding: 18, outline: "none", display: "grid", gap: 12 }}>
          <h4 style={styles.title}>{attributeMode === "edit" ? "Edit Attribute" : "Add Attribute"}</h4>
          <TextField label="Key" variant="outlined" size="small" value={attributeForm.key} onChange={e => setAttributeForm({ ...attributeForm, key: e.target.value })} />
          <TextField label="Label" variant="outlined" size="small" value={attributeForm.label} onChange={e => setAttributeForm({ ...attributeForm, label: e.target.value })} />
          <TextField
            select
            label="Value Type"
            variant="outlined"
            size="small"
            SelectProps={{ native: true }}
            value={attributeForm.value_type}
            onChange={e => setAttributeForm({ ...attributeForm, value_type: e.target.value })}
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="boolean">Boolean</option>
            <option value="json">JSON</option>
          </TextField>
          <TextField label="Unit" variant="outlined" size="small" value={attributeForm.unit} onChange={e => setAttributeForm({ ...attributeForm, unit: e.target.value })} />
          <TextField label="Static Value" variant="outlined" size="small" value={attributeForm.static_value} onChange={e => setAttributeForm({ ...attributeForm, static_value: e.target.value })} />
          {attributeError ? <div style={{ color: "#b91c1c" }}>{attributeError}</div> : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button color="white" onClick={() => setAttributeModalOpen(false)}>Cancel</Button>
            <Button color="info" onClick={saveAttribute} disabled={savingAttribute}>{savingAttribute ? "Saving..." : "Save"}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

AssetDetails.propTypes = {
  axios: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired
};
